import React, { useEffect, useLayoutEffect } from "react";
import { Trans } from "react-i18next";
import { Route, Routes, useLocation, useNavigate, useSearchParams } from "react-router-dom";

import { fetchLocation } from "@repo/shopify/api/commonApi";
import useGift from "@repo/shopify/hooks/useGift";
import useGoogleTrafficSource from "@repo/shopify/hooks/useGoogleTrafficSource";
import useMetaTrafficSource from "@repo/shopify/hooks/useMetaTrafficSource";
import useNaverTrafficSource from "@repo/shopify/hooks/useNaverTrafficSource";
import useTracking from "@repo/shopify/hooks/useTracking";

import PageLayout from "@organisms/PageLayout";

import Account from "@pages/Account";
import AccountInfo from "@pages/Account/AccountInfo";
import Addresses from "@pages/Account/Addresses";
import OrderDetail from "@pages/Account/OrderDetail";
import OrderDetailJp from "@pages/Account/OrderDetailJp";
import OrderHistory from "@pages/Account/OrderHistory";
import Checkout from "@pages/Checkout";
import CheckoutPayment from "@pages/CheckoutPayment";
import Collections from "@pages/Collections";
import Features from "@pages/Features";
import FeaturesDetail from "@pages/Features/FeaturesDetail";
import Home from "@pages/Home";
import Information from "@pages/Information";
import Contact from "@pages/Information/Contact";
import FAQ from "@pages/Information/FAQ";
import Legal from "@pages/Information/Legal";
import Membership from "@pages/Information/Membership";
import PrivacyPolicy from "@pages/Information/PrivacyPolicy";
import ShippingAndReturns from "@pages/Information/ShippingAndReturns";
import TermsAndConditions from "@pages/Information/TermsAndConditions";
import NotFound from "@pages/NotFound";
import OrderCompleted from "@pages/OrderCompleted";
import Products from "@pages/Products";
import Recruit from "@pages/Recruit";
import Refresh from "@pages/Refresh";
import ReturnRequest from "@pages/Return/ReturnRequest";
import ReturnRequested from "@pages/Return/ReturnRequested";
import SearchResult from "@pages/SearchResult";
import Stores from "@pages/Stores";
import StoresDetail from "@pages/Stores/StoresDetail";
import WeChat from "@pages/WeChat";

import { NOTIFICATIONS } from "@const/notifications";
import { DOMAIN, PREVIEW_DOMAIN, URLS } from "@const/urls";

import useCartQuery from "@hooks/useCartQuery";
import useCartSync from "@hooks/useCartSync";
import useCheckLocation from "@hooks/useCheckLocation";
import useLanguage from "@hooks/useLanguage";

import { syncHikoSessionOnBoot } from "@lib/hikoAuthSync";
import { HIKO_LOGIN_NOTICE_KEY } from "@lib/hikoHeadlessSession";

import { useCartStore } from "@store/cart";
import { useCustomerStore } from "@store/customer";
import { useLayoutStore } from "@store/layout";

import "@styles/font.css";
import "@styles/index.css";

function App() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { gtmInit, gtmLocationChanged, gtmLogin } = useTracking();
  const { lang } = useLanguage();

  useGoogleTrafficSource();
  useNaverTrafficSource();
  useMetaTrafficSource();

  const [searchParams, setSearchParams] = useSearchParams();
  const { closeAll, openMarket, openToast, openAccount, openModal } = useLayoutStore();
  const { cart, isSyncing } = useCartStore();
  const { customer, token, caaCustomer, logout, setCountryCode: setStoreCountryCode } = useCustomerStore();

  const { isPreview, isLocal, isProd, isCheckout } = useCheckLocation();
  const { addCartLine, removeCartLine, createCart } = useCartQuery();

  // 로그인 사용자 기준 카트 복원/병합
  useCartSync();

  const { addCartLines, removeCartLines } = useGift({ cart, customer });

  // 토스 임직원 확인 성공 -  ?validate=toss
  useEffect(() => {
    const tossValidate = searchParams.get("validate");
    if (tossValidate === "toss") {
      openToast(NOTIFICATIONS.SUCCESS_TOSS_VALIDATE);
      const newParams = new URLSearchParams(searchParams);
      // validate 파라미터만 제거하고 나머지(UTM, variantId 등)는 유지해야 데이터 유실이 없음
      newParams.delete("validate");
      setSearchParams(newParams);
    }
  }, [searchParams]);

  useEffect(() => {
    gtmInit();
  }, []);

  useEffect(() => {
    // 동기화 중(isSyncing)에는 구 카트 ID로 사은품 API 요청이 날아가 유실되는 것을 막기 위해 실행을 대기함
    if (!cart.id || isSyncing) return;

    if (removeCartLines.length) {
      void removeCartLine({ variables: { cartId: cart.id, lineIds: removeCartLines } });
    }

    if (addCartLines.length) {
      void addCartLine({ variables: { cartId: cart.id, lines: addCartLines } });
    }
  }, [addCartLines, removeCartLines, isSyncing]);

  // 토큰 만료 여부 체크
  useEffect(() => {
    if (token.accessToken && new Date(token.expiresAt) < new Date()) {
      openToast(NOTIFICATIONS.WARNING_END_SESSION);
      navigate(URLS.HOME);
      logout();
      setTimeout(() => {
        openAccount(true);
      }, 300);
    }
  }, []);

  // Hiko 소셜 로그인은 Shopify 호스티드 페이지를 거치는 풀 리다이렉트라서 로그인 드로어가 리다이렉트-백 시점엔 이미 언마운트되어 hiko:login 이벤트를 놓칠 수 있음
  // 부팅 시 보정. 모달/토스트는 화면 컨텍스트가 있는 여기서 처리하고, hiko:login 이벤트로 처리되는 경우(SocialLoginCaa)와 UX를 맞춤
  useEffect(() => {
    if (lang !== "jp") return;

    const showBlockedModal = () => {
      openModal({
        title: "Account Access Restricted",
        content: (
          <div>
            <Trans i18nKey="login.account_disable" />
          </div>
        ),
        confirm: {
          label: "Confirm",
          onClick: () => {
            openModal(null);
          },
        },
      });
    };

    // signin-headless 0.2.0부터 로그아웃이 Shopify 로그아웃 페이지로 리다이렉트했다가 돌아오므로,
    // acceptHikoLogin()의 반환값이 호출부(이 아래 syncHikoSessionOnBoot 쪽)에 전달되지 못하고
    // 페이지가 이미 이동해버렸을 수 있음. 왕복 후 돌아온 이번 부팅에서 그 흔적(sessionStorage)을
    // 먼저 확인 — 있으면 이번 부팅의 목적은 그 알림을 보여주는 것뿐이라 동기화는 생략한다.
    const notice = sessionStorage.getItem(HIKO_LOGIN_NOTICE_KEY);
    if (notice) {
      sessionStorage.removeItem(HIKO_LOGIN_NOTICE_KEY);
      if (notice === "blocked") showBlockedModal();
      else openToast(NOTIFICATIONS.ERROR_SERVER);
      return;
    }

    void syncHikoSessionOnBoot().then((result) => {
      if (!result) return;

      // 정상 케이스에선 hikoLogout()의 리다이렉트로 여기 도달하기 전에 페이지가 이미 이동하지만,
      // id_token이 없는 세션 등 리다이렉트가 생략되는 예외 케이스를 위해 반환값도 그대로 처리한다.
      if (result.status === "blocked") {
        showBlockedModal();
        return;
      }

      if (result.status === "error") {
        openToast(NOTIFICATIONS.ERROR_SERVER);
        return;
      }

      openToast(NOTIFICATIONS.SUCCESS_LOGIN);
      gtmLogin("social", result.customer.id.replace("gid://shopify/Customer/", ""), result.customer.emailAddress?.emailAddress);
    });
  }, []);

  useLayoutEffect(() => {
    if (!cart.id) {
      void createCart();
      return;
    }

    // jp는 classic customer가 아닌 caaCustomer(Customer Account API)로 로그인 여부를 판단해야 함
    const loggedInCustomerId = lang === "jp" ? caaCustomer?.id : customer?.id;

    // 로그아웃 후 남은 유저 카트를 버리고 빈 게스트 카트로 초기화함
    if (!loggedInCustomerId && cart?.buyerIdentity?.customer?.id) void createCart();
  }, [customer, caaCustomer, cart, lang]);

  useEffect(() => {
    gtmLocationChanged({ pathname });

    // checkout 페이지에서 querystring이 있을 때는 closeAll 호출하지 않음 (결제 에러 모달 유지)
    const hasSearchParams = searchParams.toString().length > 0;
    if (isCheckout && hasSearchParams) return;

    closeAll();
  }, [pathname]);

  useEffect(() => {
    if (window.location.origin.includes("localhost")) return;

    const getLocation = async () => {
      const response = await fetchLocation();

      if (response) {
        const countryCode = response.country;
        setStoreCountryCode(countryCode);

        // china - CN
        if (countryCode === "CN") {
          navigate("/wechat");
          localStorage.setItem("china", "true");
        }

        // 일본은 JP로 이동
        if (countryCode === "JP") {
          if (
            window.location.href.includes(PREVIEW_DOMAIN.ko.replace(/\/$/, "")) ||
            window.location.href.includes(PREVIEW_DOMAIN.en.replace(/\/$/, "")) ||
            window.location.href.includes(PREVIEW_DOMAIN.jp.replace(/\/$/, "")) ||
            window.location.href.includes(DOMAIN.jp.replace(/\/$/, ""))
          )
            return;
          window.location.href = DOMAIN.jp;
        }

        const checkLocale = localStorage.getItem("checkLocale");
        if (checkLocale) {
          const expireTime = JSON.parse(checkLocale) as number;
          if (new Date().getTime() < (expireTime || 0)) return;
        }

        // 지금 intl 사이트를 보고 있는데 KR openMarket
        if (
          window.location.origin.includes(DOMAIN.en.replace(/\/$/, "")) ||
          window.location.origin.includes(PREVIEW_DOMAIN.en.replace(/\/$/, ""))
        ) {
          if (countryCode === "KR") {
            openMarket(true);
          }
        }

        // 지금 kr 사이트를 보고 있는데 kr이 아니면 openMarket
        if (
          window.location.origin.includes(DOMAIN.ko.replace(/\/$/, "")) ||
          window.location.origin.includes(PREVIEW_DOMAIN.ko.replace(/\/$/, ""))
        ) {
          if (countryCode !== "KR") {
            openMarket(true);
          }
        }
      }
    };

    void getLocation();
  }, []);

  useEffect(() => {
    const stage = localStorage.getItem("stage");
    if (stage) return;

    if (isLocal) localStorage.setItem("stage", "local");
    if (isPreview) localStorage.setItem("stage", "preview");
    if (isProd) localStorage.setItem("stage", "prod");
  }, []);

  return (
    <Routes>
      <Route element={<PageLayout />} path={URLS.HOME}>
        <Route element={<Home />} path={URLS.HOME} />
        <Route element={<SearchResult />} path={URLS.SEARCH} />

        <Route element={<Collections />} path={URLS.COLLECTIONS} />
        <Route element={<Products />} path={URLS.PRODUCTS_DETAIL} />

        <Route element={<Checkout />} path={URLS.CHECKOUT} />
        <Route element={<CheckoutPayment />} path={URLS.CHECKOUT_PAYMENT} />
        <Route element={<OrderCompleted />} path={URLS.ORDER_COMPLETED} />

        <Route element={<Features />} path={URLS.FEATURES} />
        <Route element={<Features />} path={URLS.NEWS} />
        <Route element={<Features />} path={URLS.LOOKBOOK} />
        <Route element={<Features />} path={URLS.VIDEO} />

        <Route element={<FeaturesDetail type="news" />} path={URLS.NEWS_DETAIL} />
        <Route element={<FeaturesDetail type="lookbook" />} path={URLS.LOOKBOOK_DETAIL} />
        <Route element={<Features />} path={URLS.VIDEO_DETAIL} />

        <Route element={<Account />} path={URLS.ACCOUNT}>
          <Route element={<OrderHistory />} path={URLS.ORDER_HISTORY} />
          <Route element={lang === "jp" ? <OrderDetailJp /> : <OrderDetail />} path={URLS.ORDER_DETAIL} />
          <Route element={<AccountInfo />} path={URLS.ACCOUNT} />
          <Route element={<Addresses />} path={URLS.ADDRESSES} />
          {lang !== "jp" && (
            <>
              <Route element={<ReturnRequest />} path={URLS.RETURN_REQUEST} />
              <Route element={<ReturnRequested />} path={URLS.RETURN} />
            </>
          )}
        </Route>

        <Route element={<Stores />} path={URLS.STORES} />
        <Route element={<StoresDetail />} path={URLS.STORES_DETAIL} />

        <Route element={<Information />} path={URLS.INFORMATION}>
          <Route element={<Contact />} path={URLS.INFORMATION_CONTACT} />
          <Route element={<Membership />} path={URLS.INFORMATION_MEMBERSHIP} />
          <Route element={<ShippingAndReturns />} path={URLS.SHIPPING_AND_RETURNS} />
          <Route element={<TermsAndConditions />} path={URLS.TERMS_AND_CONDITIONS} />
          <Route element={<PrivacyPolicy />} path={URLS.PRIVACY_POLICY} />
          {lang === "jp" && (
            <>
              <Route element={<FAQ />} path={URLS.INFORMATION_FAQ} />
              <Route element={<Legal />} path={URLS.LEGAL} />
            </>
          )}
        </Route>

        {lang === "jp" && <Route element={<Recruit />} path={URLS.RECRUIT} />}
        <Route element={<WeChat />} path={URLS.WECHAT} />

        <Route element={<NotFound />} path={URLS.NOT_FOUND} />
        <Route element={<NotFound />} path="*" />

        <Route element={<Refresh />} path={URLS.REFRESH} />
      </Route>
    </Routes>
  );
}

export default App;
